import { getRiskBandDisplay, formatRiskScore, DEFAULT_ALERT_THRESHOLD } from '../lib/riskBand';
import { useUserStore } from '../store/userStore';

interface RiskBadgeProps {
  score: number | null;
  size?: 'sm' | 'md' | 'lg';
}

const sizeClasses = {
  sm: 'text-micro px-2 py-0.5',
  md: 'text-meta px-2.5 py-1',
  lg: 'text-meta px-3 py-1.5',
};

/**
 * Renders the risk band (High/Medium/Low) as the primary, always-visible
 * label. The raw score is demoted to a native hover tooltip rather than
 * shown inline — see frontend/src/lib/riskBand.ts for the banding rules.
 */
export function RiskBadge({ score, size = 'md' }: RiskBadgeProps) {
  const sizeClass = sizeClasses[size];
  // Entity's configured threshold, resolved from the already-fetched
  // session (no extra query) — falls back to the shared default when the
  // session hasn't loaded yet, or the entity has no risk config saved.
  const alertThreshold = useUserStore((s) => s.sessionConfig?.alertThreshold) ?? DEFAULT_ALERT_THRESHOLD;

  if (score === null) {
    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full font-semibold bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 ${sizeClass}`}
      >
        N/A
      </span>
    );
  }

  const { label, bgClass } = getRiskBandDisplay(score, alertThreshold);

  return (
    <span
      title={`Score: ${formatRiskScore(score)} / 5.0`}
      className={`inline-flex items-center gap-1.5 rounded-full font-semibold cursor-default ${bgClass} ${sizeClass}`}
    >
      {label}
    </span>
  );
}
